// FieldVision - MineLogfilerOversigt.tsx
// Mine Logfiler oversigt side med split-view layout: sidebar + Leaflet kort
// Viser kun marker der har uploadede tildelingslogger
// NEW 2026-01-13: Initial implementation

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
// Navigation via props (ikke react-router)
import 'leaflet/dist/leaflet.css';

import SprayWeatherWidget from './SprayWeatherWidget';
// Interface for fields with logs
interface FieldWithLogs {
  cvr: string;
  marknr: string;
  areal: number;
  logCount: number;
  geometry: any;
}

// Interface for deleted logs
interface DeletedLog {
  Id: number;
  CVR: string;
  Marknr: string;
  ApplicationDate: string;
  ProductType: string;
  TotalArea: number;
  DeletedAt: string;
}

// Helper: Naturlig/numerisk sortering af Marknr
const parseMarknr = (marknr: string): { main: number; sub: number; suffix: string } => {
  if (!marknr) return { main: 0, sub: 0, suffix: '' };

  const match = marknr.match(/^(\d+)-(\d+)([a-zA-Z]*)$/);

  if (match) {
    return {
      main: parseInt(match[1], 10),
      sub: parseInt(match[2], 10),
      suffix: match[3].toLowerCase()
    };
  }

  const numMatch = marknr.match(/^(\d+)(.*)$/);
  if (numMatch) {
    return {
      main: parseInt(numMatch[1], 10),
      sub: 0,
      suffix: numMatch[2].toLowerCase()
    };
  }

  return { main: 0, sub: 0, suffix: marknr.toLowerCase() };
};

const compareMarknr = (a: string, b: string): number => {
  const parsedA = parseMarknr(a);
  const parsedB = parseMarknr(b);

  if (parsedA.main !== parsedB.main) {
    return parsedA.main - parsedB.main;
  }

  if (parsedA.sub !== parsedB.sub) {
    return parsedA.sub - parsedB.sub;
  }

  return parsedA.suffix.localeCompare(parsedB.suffix, 'da');
};

// Map bounds updater component
const MapBoundsUpdater: React.FC<{ fields: FieldWithLogs[] }> = ({ fields }) => {
  const map = useMap();

  useEffect(() => {
    if (fields.length === 0) {
      map.setView([56.0, 10.0], 7);
      return;
    }

    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;

    for (const field of fields) {
      if (!field.geometry) continue;

      try {
        const geometry = typeof field.geometry === 'string'
          ? JSON.parse(field.geometry)
          : field.geometry;

        const coords = geometry.type === 'MultiPolygon'
          ? geometry.coordinates.flat(2)
          : geometry.coordinates.flat(1);

        for (const coord of coords) {
          if (Array.isArray(coord) && coord.length >= 2) {
            const lon = coord[0];
            const lat = coord[1];
            if (typeof lat === 'number' && typeof lon === 'number') {
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLon = Math.min(minLon, lon);
              maxLon = Math.max(maxLon, lon);
            }
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    if (minLat !== Infinity) {
      try {
        const L = (window as any).L;
        const bounds = L.latLngBounds(
          [minLat, minLon],
          [maxLat, maxLon]
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [fields, map]);

  return null;
};

// Trash Modal for deleted logs
const LogTrashModal: React.FC<{
  onClose: () => void;
  onLogRestored?: () => void;
}> = ({ onClose, onLogRestored }) => {
  const [logs, setLogs] = useState<DeletedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const loadDeletedLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/deleted`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke hente slettede logfiler');
      }

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Error loading deleted logs:', err);
      setError('Der opstod en fejl ved hentning af slettede logfiler');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeletedLogs();
  }, [loadDeletedLogs]);

  const handleRestore = async (logId: number) => {
    setRestoringId(logId);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/${logId}/restore`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke gendanne logfil');
      }

      setLogs(prevLogs => prevLogs.filter(l => l.Id !== logId));

      if (onLogRestored) {
        onLogRestored();
      }
    } catch (err) {
      console.error('Error restoring log:', err);
      setError('Der opstod en fejl ved gendannelse af logfilen');
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="trash-modal-overlay" onClick={onClose}>
      <div className="trash-modal" onClick={e => e.stopPropagation()}>
        <div className="trash-modal-header">
          <div className="trash-modal-title">
            <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="#333333" style={{verticalAlign: "middle"}}><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
            <h2>Slettede logfiler</h2>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Luk">
            &times;
          </button>
        </div>

        <div className="trash-modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Henter slettede logfiler...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadDeletedLogs} className="retry-btn">
                Proev igen
              </button>
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">&#128464;</div>
              <h3>Ingen slettede logfiler</h3>
              <p>Slettede logfiler vises her og kan gendannes.</p>
            </div>
          ) : (
            <div className="deleted-logs-list">
              <p className="list-info">
                {logs.length} {logs.length === 1 ? 'logfil' : 'logfiler'} i papirkurven
              </p>
              {logs.map(log => (
                <div key={log.Id} className="deleted-log-item">
                  <div className="log-info">
                    <div className="log-title">
                      <span className="log-type">{log.ProductType || 'Ukendt'}</span>
                      <span className="log-marknr">Mark {log.Marknr}</span>
                    </div>
                    <div className="log-meta">
                      <span>Dato: {formatDate(log.ApplicationDate)}</span>
                      <span className="deleted-date">Slettet: {formatDate(log.DeletedAt)}</span>
                    </div>
                  </div>
                  <button
                    className="restore-btn"
                    onClick={() => handleRestore(log.Id)}
                    disabled={restoringId === log.Id}
                  >
                    {restoringId === log.Id ? 'Gendanner...' : 'Gendan'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="trash-modal-footer">
          <p className="footer-note">
            Slettede logfiler kan gendannes fra papirkurven.
          </p>
        </div>
      </div>

      <style>{`
        .trash-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .trash-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 520px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .trash-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .trash-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .trash-modal-title h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #111827;
        }

        .close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: transparent;
          border: none;
          font-size: 28px;
          color: #6b7280;
          cursor: pointer;
          border-radius: 50%;
          transition: all 0.15s;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .trash-modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .loading-state,
        .error-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top-color: #16a34a;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .retry-btn {
          margin-top: 12px;
          padding: 10px 20px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .empty-icon {
          font-size: 56px;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #374151;
        }

        .empty-state p {
          margin: 0;
          font-size: 14px;
        }

        .list-info {
          margin: 0 0 16px 0;
          font-size: 14px;
          color: #6b7280;
        }

        .deleted-logs-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .deleted-log-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        .log-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .log-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .log-type {
          font-weight: 600;
          font-size: 15px;
          color: #111827;
        }

        .log-marknr {
          padding: 2px 8px;
          background: #e5e7eb;
          color: #374151;
          font-size: 12px;
          font-weight: 500;
          border-radius: 4px;
        }

        .log-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 12px;
          color: #6b7280;
        }

        .deleted-date {
          color: #dc2626;
        }

        .restore-btn {
          padding: 10px 16px;
          background: #3c6c34;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: white;
          cursor: pointer;
          transition: background 0.15s;
        }

        .restore-btn:hover:not(:disabled) {
          background: #2A4C24;
        }

        .restore-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .trash-modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .footer-note {
          margin: 0;
          font-size: 12px;
          color: #6b7280;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

// Main component

// Props interface
interface MineLogfilerOversigtProps {
  onBack: () => void;
  onNavigateToField: (marknr: string, fieldData?: { cvr: string; marknr: string; areal: number }) => void;
}

const MineLogfilerOversigt: React.FC<MineLogfilerOversigtProps> = ({ onBack, onNavigateToField }) => {
  // Navigation via props

  const [fields, setFields] = useState<FieldWithLogs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredField, setHoveredField] = useState<string | null>(null);

  // Trash modal state
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);

  const loadFieldsWithLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/fields-with-logs`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke hente marker med logfiler');
      }

      const data = await response.json();
      setFields(data.fields || []);
    } catch (err) {
      console.error('Error loading fields with logs:', err);
      setError('Kunne ikke hente marker med logfiler');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeletedCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/application-logs/deleted`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDeletedCount(data.count || 0);
      }
    } catch (err) {
      console.warn('Could not load deleted count:', err);
    }
  }, []);

  // Initial load - kun én gang
  useEffect(() => {
    loadFieldsWithLogs();
    loadDeletedCount();
  }, [loadFieldsWithLogs, loadDeletedCount]);

  // Filter and sort fields
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return fields;

    const query = searchQuery.toLowerCase();
    return fields.filter(f =>
      f.marknr?.toLowerCase().includes(query)
    );
  }, [fields, searchQuery]);

  const sortedFields = useMemo(() => {
    return [...filteredFields].sort((a, b) => {
      return compareMarknr(a.marknr || '', b.marknr || '');
    });
  }, [filteredFields]);

  // GeoJSON data for map
  const geoJsonData = useMemo(() => {
    const features = sortedFields
      .filter(f => f.geometry)
      .map(field => {
        try {
          const geometry = typeof field.geometry === 'string'
            ? JSON.parse(field.geometry)
            : field.geometry;

          return {
            type: 'Feature' as const,
            geometry,
            properties: {
              cvr: field.cvr,
              marknr: field.marknr,
              areal: field.areal,
              logCount: field.logCount
            }
          };
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);

    return { type: 'FeatureCollection' as const, features };
  }, [sortedFields]);

  // Field click handler
  const handleFieldClick = (cvr: string, marknr: string) => {
    const field = fields.find(f => f.cvr === cvr && f.marknr === marknr);
    onNavigateToField(marknr, { cvr, marknr, areal: field?.areal || 0 });
  };

  // Search handler
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  // Trash handlers
  const handleTrashClick = () => {
    setTrashModalOpen(true);
  };

  const handleTrashClose = () => {
    setTrashModalOpen(false);
  };

  const handleLogRestored = useCallback(() => {
    loadDeletedCount();
    loadFieldsWithLogs();
  }, []);

  // Get unique field key
  const getFieldKey = (cvr: string, marknr: string) => `${cvr}-${marknr}`;

  // Style function for GeoJSON
  const getStyle = (feature: any) => {
    const cvr = feature?.properties?.cvr;
    const marknr = feature?.properties?.marknr;
    const fieldKey = getFieldKey(cvr, marknr);

    const isHovered = fieldKey === hoveredField;

    return {
      fillColor: '#3B82F6',
      fillOpacity: isHovered ? 0.7 : 0.3,
      color: '#3B82F6',
      weight: isHovered ? 3 : 2,
    };
  };

  // onEachFeature for GeoJSON
  const onEachFeature = (feature: any, layer: any) => {
    const cvr = feature?.properties?.cvr;
    const marknr = feature?.properties?.marknr;
    const areal = feature?.properties?.areal;
    const fieldKey = getFieldKey(cvr, marknr);

    if (marknr) {
      layer.bindTooltip(
        `<strong>${marknr}</strong><br/>${areal?.toFixed(2) || '?'} ha`,
        {
          permanent: false,
          sticky: true,
          direction: 'top'
        }
      );
    }

    layer.on({
      click: () => {
        handleFieldClick(cvr, marknr);
      },
      mouseover: (e: any) => {
        setHoveredField(fieldKey);
        layer.openTooltip();
        e.target.setStyle({
          fillOpacity: 0.7,
          weight: 3
        });
      },
      mouseout: (e: any) => {
        setHoveredField(null);
        layer.closeTooltip();
        e.target.setStyle({
          fillOpacity: 0.3,
          weight: 2
        });
      }
    });
  };

  return (
    <div className="mine-logfiler-page">
      {/* Left sidebar */}
      <aside className="mine-logfiler-sidebar">
        <button className="back-btn" onClick={onBack}>
          <span className="back-arrow">&larr;</span>
          Tilbage til dashboard
        </button>

        <div className="sidebar-title">
          <h2>Mine logfiler</h2>
          {!loading && fields.length > 0 && (
            <div className="field-counts">
              <span className="count-badge" title="Marker med logs">
                {fields.length}
              </span>
            </div>
          )}
          {/* Trash icon */}
          <button
            className="trash-btn"
            onClick={handleTrashClick}
            title="Papirkurv - slettede logfiler"
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333333" className="trash-icon"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
            {deletedCount > 0 && (
              <span className="trash-badge">{deletedCount}</span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Henter marker med logfiler...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={loadFieldsWithLogs} className="retry-btn">
              Proev igen
            </button>
          </div>
        ) : fields.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">&#128196;</div>
            <h3>Ingen logfiler endnu</h3>
            <p>Upload tildelingslogger for at se dem her</p>
          </div>
        ) : (
          <>
            {/* Search field */}
            <form className="search-container" onSubmit={handleSearch}>
              <input
                type="text"
                className="search-input"
                placeholder="Soeg paa marknavn"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="search-btn">
                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="white" style={{verticalAlign: "middle", marginRight: "4px"}}><path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"/></svg>
                Soeg
              </button>
            </form>

            {/* Table header */}
            <div className="table-header">
              <div></div>
              <div className="col-mark">
                MARK
                <span className="sort-arrow">&#8593;</span>
              </div>
              <div className="col-logs">LOGS</div>
            </div>

            {/* Field list */}
            <div className="field-rows">
              {sortedFields.length === 0 ? (
                <div className="no-results">
                  <p>Ingen marker matcher "{searchQuery}"</p>
                </div>
              ) : (
                sortedFields.map(field => {
                  const fieldKey = getFieldKey(field.cvr, field.marknr);
                  const isHovered = hoveredField === fieldKey;

                  return (
                    <div
                      key={fieldKey}
                      className={`field-row ${isHovered ? 'hovered' : ''}`}
                      onClick={() => handleFieldClick(field.cvr, field.marknr)}
                      onMouseEnter={() => setHoveredField(fieldKey)}
                      onMouseLeave={() => setHoveredField(null)}
                    >
                      {/* Polygon icon */}
                      <div className="polygon-icon">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <path
                            d="M6 8 L16 4 L26 8 L28 24 L16 28 L4 24 Z"
                            fill="#3B82F6"
                            fillOpacity="0.3"
                            stroke="#3B82F6"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>

                      {/* Field info */}
                      <div className="field-info">
                        <span className="field-marknr">{field.marknr}</span>
                        <span className="field-areal">{field.areal?.toFixed(2)} ha</span>
                      </div>

                      {/* Log count badge */}
                      <div className="log-count-badge">
                        {field.logCount}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Field count */}
            <div className="field-count">
              {sortedFields.length} {sortedFields.length === 1 ? 'mark' : 'marker'} med logfiler
            </div>
          </>
        )}
      </aside>

      {/* Right side - Map */}
      <main className="mine-logfiler-map" style={{ position: 'relative' }}>
        <MapContainer
          center={[56.0, 10.0]}
          zoom={7}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {(geoJsonData as any).features.length > 0 && (
            <>
              <GeoJSON
                data={geoJsonData as any}
                style={getStyle}
                onEachFeature={onEachFeature}
                key={`fields-${sortedFields.length}-${hoveredField || 'none'}`}
              />
              <MapBoundsUpdater fields={sortedFields} />
            </>
          )}
        </MapContainer>
        <SprayWeatherWidget />

        {sortedFields.length === 0 && !loading && (
          <div className="map-placeholder">
            <div className="placeholder-content">
              <p>Ingen marker med logfiler at vise</p>
            </div>
          </div>
        )}
      </main>

      {/* Trash modal */}
      {trashModalOpen && (
        <LogTrashModal
          onClose={handleTrashClose}
          onLogRestored={handleLogRestored}
        />
      )}

      <style>{`
        .mine-logfiler-page {
          display: flex;
          height: calc(100vh - 60px);
          background: #f9fafb;
        }

        .mine-logfiler-sidebar {
          width: 480px;
          background: white;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 20px;
          background: white;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          text-align: left;
          font-size: 14px;
          color: #374151;
          cursor: pointer;
          transition: background 0.15s;
        }

        .back-btn:hover {
          background: #f9fafb;
        }

        .back-arrow {
          font-size: 16px;
          color: #6b7280;
        }

        .sidebar-title {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: white;
          border-bottom: 1px solid #e5e7eb;
        }

        .sidebar-title h2 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #1F2937;
        }

        .field-counts {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 8px;
          border-radius: 50%;
          font-size: 12px;
          font-weight: 600;
          background: #166534;
          color: white;
        }

        .trash-btn {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #f3f4f6;
          border: none;
          cursor: pointer;
          transition: all 0.15s;
          margin-left: auto;
        }

        .trash-btn:hover {
          background: #e5e7eb;
        }

        .trash-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 4px;
          background: #EF4444;
          color: white;
          font-size: 11px;
          font-weight: 600;
          border-radius: 9px;
        }

        .search-container {
          display: flex;
          gap: 8px;
          padding: 12px 20px;
          background: white;
          border-bottom: 1px solid #e5e7eb;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-size: 14px;
          color: #374151;
          height: 40px;
          box-sizing: border-box;
        }

        .search-input:focus {
          outline: none;
          border-color: #16a34a;
          box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
        }

        .search-input::placeholder {
          color: #9ca3af;
        }

        .search-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: #166534;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
          height: 40px;
        }

        .search-btn:hover {
          opacity: 0.9;
        }

        .table-header {
          display: grid;
          grid-template-columns: 48px 1fr 60px;
          gap: 8px;
          padding: 8px 20px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .col-mark {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .sort-arrow {
          color: #16a34a;
        }

        .col-logs {
          text-align: center;
        }

        .field-rows {
          flex: 1;
          overflow-y: auto;
        }

        .field-row {
          display: grid;
          grid-template-columns: 48px 1fr 60px;
          gap: 8px;
          align-items: center;
          padding: 12px 20px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background 0.15s;
        }

        .field-row:hover,
        .field-row.hovered {
          background: #F9FAFB;
        }

        .polygon-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .field-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .field-marknr {
          font-size: 16px;
          font-weight: 500;
          color: #1F2937;
        }

        .field-areal {
          font-size: 14px;
          color: #16A34A;
        }

        .log-count-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #F3F4F6;
          color: #6B7280;
          border-radius: 50%;
          font-size: 12px;
          font-weight: 600;
          margin: 0 auto;
        }

        .no-results {
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .no-results p {
          margin: 0;
          font-size: 14px;
        }

        .field-count {
          padding: 10px 20px;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #6b7280;
        }

        .loading-state,
        .error-state,
        .empty-state {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top-color: #16a34a;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .retry-btn {
          margin-top: 12px;
          padding: 10px 20px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #2A4C24;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          font-size: 16px;
          color: #374151;
        }

        .empty-state p {
          margin: 0;
          font-size: 14px;
        }

        .mine-logfiler-map {
          flex: 1;
          position: relative;
          background: #e5e7eb;
          min-width: 400px;
        }

        .map-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          z-index: 0;
        }

        .placeholder-content {
          text-align: center;
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default MineLogfilerOversigt;
